/**
 * Study list content - shows saved studies with multi-select for bulk delete.
 * Supports shift-click for range selection. Create/Import live in the panel toolbar.
 */

import { useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { TrashIcon } from '@components/icons';
import { formatReference, formatShortReference } from '@skjv/common';
import { StudyRow } from './study-row';
import { buildYearGroups, type StudyListRow } from './group-by-year';

function buildHaystack(row: StudyListRow): string {
  const parts = [row.name, formatReference(row.startingLocation), formatShortReference(row.startingLocation)];
  if (row.endingLocation) {
    parts.push(formatReference(row.endingLocation), formatShortReference(row.endingLocation));
  }
  return parts.join(' ').toLowerCase();
}

export const StudyList = observer(function StudyList() {
  const studies = bibleState.studies;
  const indexEntries = bibleState.studyIndex.entries;
  const [searchQuery, setSearchQuery] = useState('');
  const allGroups = useMemo(() => buildYearGroups(studies, indexEntries), [studies, indexEntries]);
  const allRows = useMemo(() => allGroups.flatMap((g) => g.rows), [allGroups]);
  const groups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups
      .map((g) => ({ year: g.year, rows: g.rows.filter((r) => buildHaystack(r).includes(q)) }))
      .filter((g) => g.rows.length > 0);
  }, [allGroups, searchQuery]);
  const flatRows = useMemo(() => groups.flatMap((g) => g.rows), [groups]);
  const flatIndexById = useMemo(() => {
    const map = new Map<string, number>();
    flatRows.forEach((r, i) => map.set(r.id, i));
    return map;
  }, [flatRows]);
  const localRows = useMemo(() => flatRows.filter((r) => r.source !== 'remote'), [flatRows]);

  // Year accordions (UAT 2026-09-02): the newest year opens by default,
  // prior years sit shut and render NO rows until twisted open. Defaults
  // are derived (not effect-initialized) so late-arriving studies from the
  // deferred library hydration keep the right year open. An active search
  // reaches into shut years.
  const newestYear = allGroups[0]?.year;
  const [toggledYears, setToggledYears] = useState<Map<number, boolean>>(new Map());
  const isSearching = searchQuery.trim().length > 0;
  const isYearOpen = (year: number) => toggledYears.get(year) ?? year === newestYear;
  const toggleYear = (year: number) =>
    setToggledYears((prev) => new Map(prev).set(year, !isYearOpen(year)));

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  const isSelecting = selectedIds.size > 0;

  const handleRangeSelection = (id: string, index: number, e: React.MouseEvent) => {
    const newSet = new Set(selectedIds);
    if (e.shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index);
      const end = Math.max(lastSelectedIndex, index);
      for (let i = start; i <= end; i++) {
        const r = flatRows[i];
        if (r && r.source !== 'remote') newSet.add(r.id);
      }
    } else {
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
    }
    setSelectedIds(newSet);
    setLastSelectedIndex(index);
  };

  const handleRowClick = (id: string, index: number, e: React.MouseEvent) => {
    if (isSelecting) {
      handleRangeSelection(id, index, e);
      return;
    }
    const row = flatRows[index];
    if (!row || row.source === 'remote') {
      bibleState.showToast('On Drive — run Sync now to pull it locally first.', 'info');
      return;
    }
    bibleState.startEditStudy(id);
  };

  const selectAll = () => {
    if (selectedIds.size === localRows.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(localRows.map((r) => r.id)));
  };

  const deleteSelected = () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    bibleState.showConfirmDialog({
      title: 'Delete Studies',
      message: `Delete ${count} selected stud${count === 1 ? 'y' : 'ies'}?`,
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: () => {
        for (const id of selectedIds) bibleState.removeStudy(id);
        setSelectedIds(new Set());
        setLastSelectedIndex(null);
      },
    });
  };

  const cancelSelection = () => {
    setSelectedIds(new Set());
    setLastSelectedIndex(null);
  };

  return (
    // The list owns its scroll: search/selection sit as a fixed header and
    // only the rows scroll below them (UAT 2026-08-26 — the scrollbar used
    // to run up beside the search bar).
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Selection header */}
      {isSelecting && (
        <div className="flex items-center justify-between rounded-xl border border-cyan-200 bg-cyan-50 p-2 shadow-md dark:border-cyan-700/30 dark:bg-cyan-900">
          <div className="flex items-center gap-2">
            <button
              onClick={selectAll}
              className="rounded bg-cyan-100 px-2 py-1 text-xs text-cyan-700 transition-colors hover:bg-cyan-200 dark:bg-cyan-800/40 dark:text-cyan-300 dark:hover:bg-cyan-700/40"
            >
              {selectedIds.size === localRows.length ? 'Deselect all' : 'Select all'}
            </button>
            <span className="text-sm text-cyan-700 dark:text-cyan-300">
              {selectedIds.size} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={cancelSelection}
              className="rounded px-2 py-1 text-xs text-stone-600 transition-colors hover:bg-stone-200 dark:text-stone-400 dark:hover:bg-stone-700"
            >
              Cancel
            </button>
            <button
              onClick={deleteSelected}
              className="flex items-center gap-1 rounded bg-red-100 px-2 py-1 text-xs text-red-700 transition-colors hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-800/40"
            >
              <TrashIcon className="h-3 w-3" />
              Delete
            </button>
          </div>
        </div>
      )}

      {isSelecting && (
        <p className="text-center text-xs text-stone-400 dark:text-stone-500">
          Shift+click to select a range
        </p>
      )}

      {/* Search — fixed above the scroll region */}
      {!isSelecting && allRows.length > 0 && (
        <div className="-mx-1 px-1 py-1">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="search by name or Bible location..."
            className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 placeholder-stone-400 focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-200"
          />
        </div>
      )}

      {/* Deferred library hydration — the shell paints scripture first and
          the library streams in behind it; a stale mirror copy (if any)
          stays visible under this status until the reconcile lands. */}
      {bibleState.isLibraryLoading && (
        <div className="study-library-loading flex items-center justify-center gap-2 py-1 text-sm text-stone-500 dark:text-stone-400">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-stone-300 border-t-cyan-500 dark:border-stone-600 dark:border-t-cyan-400" />
          Opening your study library&hellip;
        </div>
      )}

      {/* Studies list — the only scrolling region; year headers stick to its top */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
      {allRows.length === 0 && bibleState.isLibraryLoading ? null : allRows.length === 0 ? (
        <div className="py-8 text-center text-stone-500 dark:text-stone-400">
          <p>No studies yet.</p>
          <p className="mt-2 text-sm">Create your first Bible study or import existing ones.</p>
          <button
            onClick={() => bibleState.startGuide()}
            className="mt-4 inline-flex max-w-[16rem] items-center gap-2 rounded-xl border border-cyan-300/50 bg-cyan-50/80 px-4 py-2.5 text-sm font-medium text-cyan-700 transition-all hover:bg-cyan-100/80 dark:border-cyan-700/40 dark:bg-cyan-900/20 dark:text-cyan-300 dark:hover:bg-cyan-900/40"
          >
            New here? Take the guided tour
          </button>
        </div>
      ) : flatRows.length === 0 ? (
        <div className="py-8 text-center text-sm text-stone-500 dark:text-stone-400">
          <p>No studies match &ldquo;{searchQuery.trim()}&rdquo;.</p>
          <button
            onClick={() => setSearchQuery('')}
            className="mt-3 text-xs text-cyan-600 hover:underline dark:text-cyan-400"
          >
            Clear search
          </button>
        </div>
      ) : (
        groups.map((group) => {
          const isOpen = isSearching || isYearOpen(group.year);
          return (
            <div key={group.year} className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => toggleYear(group.year)}
                className="study-year-header sticky top-0 z-[5] -mx-1 flex items-center gap-1.5 bg-stone-50/85 px-2 py-1 text-left text-xs font-semibold uppercase tracking-wide text-stone-500 backdrop-blur-sm dark:bg-stone-900/70 dark:text-stone-400"
              >
                <span
                  className={`inline-block transition-transform duration-150 ${isOpen ? 'rotate-90' : ''}`}
                >
                  ▸
                </span>
                {group.year}{' '}
                <span className="font-normal text-stone-400">({group.rows.length})</span>
              </button>
              {isOpen &&
                group.rows.map((row) => (
                  <StudyRow
                    key={row.id}
                    row={row}
                    index={flatIndexById.get(row.id) ?? 0}
                    isSelected={selectedIds.has(row.id)}
                    isSelecting={isSelecting}
                    onToggleSelection={handleRangeSelection}
                    onRowClick={handleRowClick}
                  />
                ))}
            </div>
          );
        })
      )}
      </div>

    </div>
  );
});
